import React, { useCallback, useMemo } from 'react';
import type { ConfiguredService } from '../../types/service.types';
import type { ConfiguredAddOn } from '../../types/addon.types';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import { useAvailableAddOns, useLinkedAddOnSync } from '../../hooks';
import { buildAddOnInstances, instanceOrdinals } from '../../utils/addOnInstances';
import { instanceIdMinter } from '../../utils/instanceIds';
import { useQuote } from '@/features/quote/context/QuoteContext';
import { AddOnCard } from './AddOnCard/AddOnCard';
import AddOnItem from './AddOnItem';

interface AddOnConfiguratorProps {
    configuredServices: ConfiguredService[];
    configuredAddOns: ConfiguredAddOn[];
    setConfiguredAddOns: React.Dispatch<React.SetStateAction<ConfiguredAddOn[]>>;
    className?: string;
}

const EMPTY_INSTANCES: ConfiguredAddOn[] = [];
const noop = () => {};

export const AddOnConfigurator: React.FC<AddOnConfiguratorProps> = ({
    configuredServices,
    configuredAddOns,
    setConfiguredAddOns,
    className,
}) => {
    const { selectedProduct, dismissAutoAddOn } = useQuote();
    const currency = getProductCurrency(selectedProduct);
    // Defensive check to ensure configuredAddOns is always an array
    const safeConfiguredAddOns = useMemo(
        () => (Array.isArray(configuredAddOns) ? configuredAddOns : []),
        [configuredAddOns]
    );
    const safeConfiguredServices = useMemo(
        () => (Array.isArray(configuredServices) ? configuredServices : []),
        [configuredServices]
    );
    const { addons, isLoading, isPlaceholderData, error, getAddOnById } = useAvailableAddOns(
        selectedProduct?.id
    );

    useLinkedAddOnSync({
        configuredServices: safeConfiguredServices,
        configuredAddOns: safeConfiguredAddOns,
        setConfiguredAddOns,
        selectedLinkedServiceId: 'independent',
        onLinkedServiceUnavailable: noop,
    });

    const instancesByAddOn = useMemo(() => {
        const grouped = new Map<string, ConfiguredAddOn[]>();
        safeConfiguredAddOns.forEach((entry) => {
            const existing = grouped.get(entry.addOnId);
            if (existing) existing.push(entry);
            else grouped.set(entry.addOnId, [entry]);
        });
        return grouped;
    }, [safeConfiguredAddOns]);

    const orphanedInstances = useMemo(
        () => safeConfiguredAddOns.filter((entry) => !addons.some((a) => a.id === entry.addOnId)),
        [safeConfiguredAddOns, addons]
    );

    const orphanOrdinals = useMemo(() => instanceOrdinals(orphanedInstances), [orphanedInstances]);

    const handleAdd = useCallback(
        (addOnId: string, schedule: string, date: Date | null) => {
            const addOn = getAddOnById(addOnId);
            if (!addOn) return;
            setConfiguredAddOns((prev) => {
                const built = buildAddOnInstances(
                    addOn,
                    schedule,
                    date,
                    safeConfiguredServices,
                    instanceIdMinter(prev)
                );
                return built.length === 0 ? prev : [...prev, ...built];
            });
        },
        [getAddOnById, safeConfiguredServices, setConfiguredAddOns]
    );

    const handleRemove = useCallback(
        (instanceId: string | number) => {
            const removed = safeConfiguredAddOns.find((addOn) => addOn.instanceId === instanceId);
            if (removed && removed.source === 'auto' && !removed.mandatory) {
                dismissAutoAddOn(removed.addOnId);
            }
            setConfiguredAddOns((prev) => prev.filter((addOn) => addOn.instanceId !== instanceId));
        },
        [safeConfiguredAddOns, setConfiguredAddOns, dismissAutoAddOn]
    );

    // The retained previous product's rows are not this product's catalogue, so
    // they must not be offerable while the new assignments are still in flight.
    if (isLoading || isPlaceholderData) {
        return (
            <div
                className={`rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 ${className}`}
            >
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    2. Add Optional Extras
                </h2>
                <div className="flex items-center justify-center py-8">
                    <div className="text-mp-muted">Loading optional extras...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className={`rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 ${className}`}
            >
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    2. Add Optional Extras
                </h2>
                <div className="flex items-center justify-center py-8">
                    <div className="text-mp-error">
                        Error loading optional extras. Please try again.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 ${className}`}
        >
            <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                2. Add Optional Extras
            </h2>
            {addons.length === 0 ? (
                <p className="text-mp-muted text-center py-4 bg-mp-background rounded-lg">
                    No optional extras available for this product
                </p>
            ) : (
                <div className="flex flex-col gap-3">
                    {addons.map((addon) => (
                        <AddOnCard
                            key={addon.id}
                            addOn={addon}
                            instances={instancesByAddOn.get(addon.id) ?? EMPTY_INSTANCES}
                            services={safeConfiguredServices}
                            currency={currency}
                            onAdd={handleAdd}
                            onRemove={handleRemove}
                        />
                    ))}
                </div>
            )}

            {orphanedInstances.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-base font-semibold text-mp-foreground mb-2">
                        Optional extras no longer offered for this product
                    </h3>
                    {orphanedInstances.map((instance, index) => (
                        <AddOnItem
                            key={instance.instanceId}
                            addOn={instance}
                            onRemove={handleRemove}
                            serviceList={safeConfiguredServices}
                            currency={currency}
                            instanceIndex={orphanOrdinals[index].index}
                            instanceCount={orphanOrdinals[index].count}
                            showName
                            forceRemovable
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
